import { useCalendarEdit } from "./CalendarEditContext";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";
import { 
  Pencil, 
  Eye, 
  X, 
  Save,
  AlertTriangle,
  Info,
  Loader2,
  CheckCircle2,
  RotateCcw,
} from "lucide-react";

/**
 * CalendarEditControls - Toggle and status controls for calendar edit mode
 * Shows edit/view mode toggle, save/discard buttons, and unsaved changes indicator
 */
export function CalendarEditControls({ className }) {
  const {
    isEditMode,
    toggleEditMode,
    hasUnsavedChanges,
    isSaving,
    saveChanges,
    discardChanges,
    pendingChanges,
  } = useCalendarEdit();

  const changeCount = pendingChanges?.size || 0;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/* Save Button - only show in edit mode with unsaved changes */}
      {isEditMode && hasUnsavedChanges && (
        <Button
          variant="default"
          size="sm"
          onClick={saveChanges}
          disabled={isSaving}
          className="gap-2 bg-emerald-600 hover:bg-emerald-700"
          data-testid="calendar-save-changes"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save ({changeCount})
            </>
          )}
        </Button>
      )}
      
      {/* Discard Button - only show in edit mode with unsaved changes */}
      {isEditMode && hasUnsavedChanges && (
        <Button
          variant="outline"
          size="sm"
          onClick={discardChanges}
          disabled={isSaving}
          className="gap-2 text-muted-foreground hover:text-foreground"
          data-testid="calendar-discard-changes"
        >
          <RotateCcw className="h-4 w-4" />
          Discard
        </Button>
      )}

      {/* Mode Toggle Button */}
      <Button
        variant={isEditMode ? "default" : "outline"}
        size="sm"
        onClick={toggleEditMode}
        disabled={isSaving}
        className={cn(
          "gap-2 transition-all",
          isEditMode && "bg-primary hover:bg-primary/90"
        )}
        data-testid="calendar-edit-mode-toggle"
      >
        {isEditMode ? (
          <>
            <Eye className="h-4 w-4" />
            Exit Edit Mode
          </>
        ) : (
          <>
            <Pencil className="h-4 w-4" />
            Edit Mode
          </>
        )}
      </Button>

      {/* Unsaved Changes Badge */}
      {isEditMode && hasUnsavedChanges && (
        <Badge 
          variant="outline" 
          className="bg-amber-500/10 text-amber-500 border-amber-500/30 gap-1"
        >
          <AlertTriangle className="h-3 w-3" />
          {changeCount} unsaved
        </Badge>
      )}
    </div>
  );
}

/**
 * CalendarEditModeBanner - Banner shown when in edit mode
 * Provides context, instructions, and save/discard controls
 */
export function CalendarEditModeBanner({ className }) {
  const { 
    isEditMode, 
    discardChanges, 
    saveChanges,
    hasUnsavedChanges, 
    isSaving,
    pendingChanges,
    validationErrors,
  } = useCalendarEdit();

  if (!isEditMode) return null;

  const changeCount = pendingChanges?.size || 0;
  const errorCount = validationErrors?.size || 0;

  return (
    <div 
      className={cn(
        "bg-primary/10 border border-primary/30 rounded-lg p-3 flex items-center justify-between gap-4",
        hasUnsavedChanges && "border-amber-500/30 bg-amber-500/5",
        errorCount > 0 && "border-red-500/30 bg-red-500/5",
        className
      )}
      data-testid="calendar-edit-mode-banner"
    >
      <div className="flex items-center gap-3">
        <div className={cn(
          "p-2 rounded-full",
          errorCount > 0 ? "bg-red-500/20" : hasUnsavedChanges ? "bg-amber-500/20" : "bg-primary/20"
        )}>
          {errorCount > 0 ? (
            <AlertTriangle className="h-4 w-4 text-red-500" />
          ) : hasUnsavedChanges ? (
            <Pencil className="h-4 w-4 text-amber-500" />
          ) : (
            <Pencil className="h-4 w-4 text-primary" />
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">
            {errorCount > 0 
              ? `Edit Mode — ${errorCount} validation error${errorCount !== 1 ? 's' : ''}`
              : hasUnsavedChanges 
                ? `Edit Mode — ${changeCount} pending change${changeCount !== 1 ? 's' : ''}`
                : "Edit Mode Active"
            }
          </p>
          <p className="text-xs text-muted-foreground">
            {errorCount > 0 
              ? "Fix errors or revert changes before saving"
              : hasUnsavedChanges
                ? "Click Save to persist changes or Discard to revert"
                : "Click shifts to select • Drag to move • Resize edges to adjust times"
            }
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {/* Status indicator */}
        {!hasUnsavedChanges && (
          <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground bg-surface-elevated/50 px-2 py-1 rounded">
            <Info className="h-3 w-3" />
            No changes yet
          </div>
        )}
        
        {/* Save button in banner */}
        {hasUnsavedChanges && (
          <Button
            variant="default"
            size="sm"
            onClick={saveChanges}
            disabled={isSaving}
            className="gap-1 bg-emerald-600 hover:bg-emerald-700"
            data-testid="calendar-banner-save"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-3 w-3" />
                Save Changes
              </>
            )}
          </Button>
        )}
        
        {/* Discard button in banner */}
        {hasUnsavedChanges && (
          <Button
            variant="ghost"
            size="sm"
            onClick={discardChanges}
            disabled={isSaving}
            className="gap-1 text-muted-foreground hover:text-foreground"
            data-testid="calendar-banner-discard"
          >
            <X className="h-3 w-3" />
            Discard
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * CalendarEditLegend - Legend for edit mode visual indicators
 */
export function CalendarEditLegend({ className }) {
  const { isEditMode, hasUnsavedChanges } = useCalendarEdit();

  if (!isEditMode) return null;

  return (
    <div className={cn("flex flex-wrap gap-4 text-xs", className)}>
      <div className="flex items-center gap-1.5">
        <div className="w-4 h-4 rounded border-2 border-primary bg-primary/10" />
        <span className="text-muted-foreground">Selected</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-4 h-4 rounded border-2 border-dashed border-primary/50 bg-primary/5" />
        <span className="text-muted-foreground">Hover to edit</span>
      </div>
      {hasUnsavedChanges && (
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded border-2 border-amber-500 bg-amber-500/20" />
          <span className="text-muted-foreground">Modified</span>
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <div className="w-4 h-4 rounded border-2 border-amber-500 bg-amber-500/10" />
        <span className="text-muted-foreground">Warning</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-4 h-4 rounded border-2 border-red-500 bg-red-500/10" />
        <span className="text-muted-foreground">Error</span>
      </div>
    </div>
  );
}

/**
 * ShiftValidationError - Display validation error for a specific shift
 */
export function ShiftValidationError({ shiftId, className }) {
  const { getValidationError, revertShiftChange } = useCalendarEdit();
  const error = getValidationError(shiftId);
  
  if (!error) return null;
  
  return (
    <div 
      className={cn(
        "absolute -top-8 left-0 right-0 z-30 bg-red-500/95 text-white text-[10px] px-2 py-1 rounded shadow-lg flex items-center justify-between gap-2",
        className
      )}
      data-testid={`shift-error-${shiftId}`}
    >
      <span className="truncate">{error}</span>
      <button 
        onClick={(e) => {
          e.stopPropagation();
          revertShiftChange(shiftId);
        }}
        className="shrink-0 hover:text-red-200"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

export default CalendarEditControls;
